import { FCElement } from '@fc/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@fc/utilities';

const PREVIEW_HOSTS = new Set(['127.0.0.1', 'localhost', '[::1]']);

// Localization form with country/language selection, search, and keyboard nav
class LocalizationFormComponent extends FCElement {
  #ac = null;

  connectedCallback() {
    super.connectedCallback();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const disclosure = this.closest('details');

    this.refs.search?.addEventListener('keydown', this.#onSearchKey, { signal });
    this.refs.search?.addEventListener('focus', this.#onPrimaryFocus, { signal });

    if (this.refs.countryList) {
      this.refs.countryList.addEventListener('keydown', this.#onListKey, { signal });
      this.refs.countryList.addEventListener('focus', this.#onCountryListFocus, { signal });
    }

    const scrollRegion = this.#getScrollRegion();
    if (scrollRegion) {
      scrollRegion.addEventListener('scroll', (e) => requestAnimationFrame(() => this.#onScroll(e)), {
        passive: true,
        signal,
      });
    }

    if (disclosure instanceof HTMLDetailsElement) {
      disclosure.addEventListener('toggle', this.#onDisclosureToggle, { signal });
    }

    if (this.refs.countryListItems?.length) {
      this.filterCountries();
    }
    this.resizeLanguageInput();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
    this.#ac = null;
  }

  #onListKey = (event) => {
    const { countryListItems } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#moveFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#moveFocus('DOWN');
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        this.#moveFocus('FIRST');
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        this.#moveFocus('LAST');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        this.#submitSelectedCountry();
        break;
      }
      case ' ': {
        if (!(document.activeElement instanceof HTMLElement) || document.activeElement === this.refs.search) break;
        event.preventDefault();
        event.stopPropagation();
        this.#submitSelectedCountry();
        break;
      }
      case 'Escape':
        if (this.#dismissOwningSurface()) {
          event.preventDefault();
          event.stopPropagation();
        }
        break;
    }

    // Update aria-activedescendant after DOM settles
    setTimeout(() => {
      const active = this.refs.countryListItems.find((el) => el.getAttribute('aria-selected') === 'true');
      this.#getActiveDescendantTarget()?.setAttribute('aria-activedescendant', active?.id ?? '');
    });
  };

  #getVisibleCountryItems() {
    return this.refs.countryListItems.filter((el) => !el.hasAttribute('hidden'));
  }

  #getSelectedCountryItem() {
    return this.#getVisibleCountryItems().find((el) => el.getAttribute('aria-selected') === 'true') || null;
  }

  #getScrollRegion() {
    return this.querySelector('.country-selector-form__wrapper');
  }

  #scrollItemIntoRegion(item, { block = 'nearest' } = {}) {
    if (!(item instanceof HTMLElement)) return;

    const scrollRegion = this.#getScrollRegion();
    if (!(scrollRegion instanceof HTMLElement)) {
      item.scrollIntoView({ block });
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const regionRect = scrollRegion.getBoundingClientRect();
    const itemTop = itemRect.top - regionRect.top + scrollRegion.scrollTop;
    const itemBottom = itemTop + itemRect.height;
    const visibleTop = scrollRegion.scrollTop;
    const visibleBottom = visibleTop + scrollRegion.clientHeight;

    if (block === 'center') {
      const centeredTop = itemTop - (scrollRegion.clientHeight - itemRect.height) / 2;
      const maxTop = Math.max(0, scrollRegion.scrollHeight - scrollRegion.clientHeight);
      scrollRegion.scrollTop = Math.min(Math.max(centeredTop, 0), maxTop);
      return;
    }

    if (itemTop < visibleTop) {
      scrollRegion.scrollTop = itemTop;
      return;
    }

    if (itemBottom > visibleBottom) {
      scrollRegion.scrollTop = itemBottom - scrollRegion.clientHeight;
    }
  }

  #focusWithoutScroll(element) {
    if (!(element instanceof HTMLElement)) return;

    try {
      element.focus({ preventScroll: true });
    } catch {
      element.focus();
    }
  }

  #revealSelectedCountryItem({ block = 'center' } = {}) {
    this.#scrollItemIntoRegion(this.#getSelectedCountryItem(), { block });
  }

  #onDisclosureToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement) || !event.target.open) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncPrimaryControl({ block: 'center' }));
    });
  };

  #onPrimaryFocus = () => {
    this.syncPrimaryControl({ block: 'center' });
  };

  #onCountryListFocus = () => {
    this.#syncActiveCountryItem();
  };

  #getActiveDescendantTarget() {
    const { search, countryList } = this.refs;
    const active = document.activeElement;

    if (countryList instanceof HTMLElement && (active === countryList || countryList.contains(active))) {
      return countryList;
    }

    return search || countryList || null;
  }

  #submitSelectedCountry({ allowCurrent = false } = {}) {
    const { countryInput, form, search } = this.refs;
    const active = this.#getSelectedCountryItem();
    const nextValue = active?.dataset.value ?? '';
    const currentValue = countryInput?.value ?? '';
    const hasQuery = normalizeString(search?.value ?? '') !== '';

    if (!countryInput || !form || !nextValue) return false;
    if (!allowCurrent && !hasQuery && nextValue === currentValue) return false;

    countryInput.value = nextValue;
    this.#submitLocalizationForm(form);
    return true;
  }

  #dismissOwningSurface() {
    const drawer = this.closest('nav-drawer');
    if (drawer && typeof drawer.dismiss === 'function') {
      drawer.dismiss({ reason: 'escape-key' });
      return true;
    }

    const dropdown = this.closest('locale-dropdown');
    if (dropdown && typeof dropdown.hidePanel === 'function') {
      dropdown.hidePanel();
      return true;
    }

    const details = this.closest('details[open]');
    const closeControl = details?.querySelector('.fit-header-surface-close, .menu-drawer__close-button');
    if (closeControl instanceof HTMLElement) {
      closeControl.click();
      return true;
    }

    return false;
  }

  #shouldUseUrlFallback() {
    return PREVIEW_HOSTS.has(window.location.hostname) || window.Shopify?.designMode === true;
  }

  #submitLocalizationForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;

    if (this.#shouldUseUrlFallback()) {
      const formData = new FormData(form);
      const url = new URL(window.location.href);
      const countryCode = formData.get('country_code');
      const languageCode = formData.get('language_code');

      if (typeof countryCode === 'string' && countryCode) {
        url.searchParams.set('country', countryCode);
      }

      if (typeof languageCode === 'string' && languageCode) {
        url.searchParams.set('locale', languageCode);
      }

      if (url.href === window.location.href) {
        window.location.reload();
      } else {
        window.location.assign(url.toString());
      }

      return true;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }

    return true;
  }

  #syncActiveCountryItem({ focus = false } = {}) {
    const { countryInput } = this.refs;
    const visible = this.#getVisibleCountryItems();
    const activeDescendantTarget = this.#getActiveDescendantTarget();

    if (!visible.length) {
      activeDescendantTarget?.setAttribute('aria-activedescendant', '');
      return;
    }

    const selectedValue = countryInput?.value ?? '';
    const next =
      visible.find((el) => el.dataset.value === selectedValue) ||
      visible.find((el) => el.getAttribute('aria-current') === 'true') ||
      visible[0];

    for (const el of this.refs.countryListItems) {
      el.setAttribute('aria-selected', String(el === next));
    }

    activeDescendantTarget?.setAttribute('aria-activedescendant', next?.id ?? '');

    if (focus && next instanceof HTMLElement) {
      const focusTarget = activeDescendantTarget instanceof HTMLElement ? activeDescendantTarget : next;
      this.#focusWithoutScroll(focusTarget);
      this.#scrollItemIntoRegion(next, { block: 'nearest' });
    }
  }

  selectCountry = (countryCode, event) => {
    event?.preventDefault();
    const { countryInput, form } = this.refs;
    if (!countryInput) return;
    if (typeof countryCode === 'string') countryInput.value = countryCode;
    this.#submitLocalizationForm(form);
  };

  changeLanguage(event) {
    if (!event?.target) return;
    const { form, languageInput } = this.refs;
    if (!form || !languageInput) return;

    const value = event.target.value;
    if (!value) return;

    languageInput.value = value;
    this.resizeLanguageInput();
    this.#submitLocalizationForm(form);
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;
    if (!languageInput) return;

    // Temporarily hide non-selected option text to measure selected width
    for (const opt of languageInput.options) {
      if (!opt.selected) {
        opt.dataset.optionLabel = opt.textContent ?? '';
        opt.innerText = '';
      }
    }

    // Measure and fix width based on selected option
    languageInput.style.width = 'fit-content';
    const w = languageInput.offsetWidth;
    if (w > 0) languageInput.style.width = `${Math.ceil(w) + 1}px`;

    // Restore all option labels
    for (const opt of languageInput.options) {
      if (opt.dataset.optionLabel) {
        opt.textContent = opt.dataset.optionLabel;
        delete opt.dataset.optionLabel;
      }
    }
  }

  #findMatches(
    searchValue,
    countryEl,
    options = {
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      labelMatchStart: false,
      aliasExactMatch: false,
    },
  ) {
    const result = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const name = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');
      if (!name) return result;
      result.label = options.labelMatchStart ? name.startsWith(searchValue) : name.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      result.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      result.iso = normalizeString(iso ?? '') === searchValue;
    }

    if (options.matchAlias) {
      const parsed = aliases?.split(',').map((a) => normalizeString(a));
      if (!parsed) return result;
      result.alias =
        parsed.length > 0 &&
        parsed.some((a) => (options.aliasExactMatch ? a === searchValue : a.startsWith(searchValue)));
    }

    return result;
  }

  #highlight(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const norm = normalizeString(text);
    const normSearch = normalizeString(searchValue);
    const idx = norm.indexOf(normSearch);
    if (idx === -1) return text;

    const end = idx + normSearch.length;
    const before = text.slice(0, idx);
    const match = text.slice(idx, end);
    const after = text.slice(end);

    let out = '';
    if (before) out += `<mark>${before}</mark>`;
    out += match;
    if (after) out += `<mark>${after}</mark>`;
    return out;
  }

  filterCountries() {
    if (!this.refs.countryListItems?.length) return;
    const { countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const query = normalizeString(search?.value ?? '');
    let visible = 0;

    resetButton?.toggleAttribute('hidden', !query);
    popularCountries?.toggleAttribute('hidden', Boolean(query));

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    wrapper?.classList.toggle('is-searching', !!query);

    for (const el of countryListItems) {
      if (!query) {
        const isPopularDuplicate = el.dataset.isPopularDuplicate === 'true';
        el.toggleAttribute('hidden', isPopularDuplicate);
        const span = el.querySelector('.country');
        // Reset innerHTML by reassigning textContent
        if (span) span.textContent = span.textContent;
        if (!isPopularDuplicate) visible++;
        continue;
      }

      const matches = this.#findMatches(query, el);
      if (matches.label || matches.alias || matches.iso || matches.currency) {
        el.removeAttribute('hidden');
        const span = el.querySelector('.country');
        if (span) span.innerHTML = this.#highlight(span.textContent, query);
        visible++;
      } else {
        el.setAttribute('hidden', '');
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${visible}`);
    }

    noResultsMessage.hidden = visible > 0;
    const scrollRegion = this.#getScrollRegion();
    if (scrollRegion) scrollRegion.scrollTop = 0;
    this.#syncActiveCountryItem();
  }

  #moveFocus(direction) {
    const items = this.#getVisibleCountryItems();
    if (!items.length) return;

    const active = items.find((el) => el.getAttribute('aria-selected') === 'true') || items[0];
    const currentIndex = items.findIndex((el) => el === active);
    let next = active;

    switch (direction) {
      case 'UP':
        next = currentIndex > 0 ? items[currentIndex - 1] : items[items.length - 1];
        break;
      case 'DOWN':
        next = currentIndex < items.length - 1 ? items[currentIndex + 1] : items[0];
        break;
      case 'FIRST':
        next = items[0];
        break;
      case 'LAST':
        next = items[items.length - 1];
        break;
    }

    for (const el of this.refs.countryListItems) {
      el.setAttribute('aria-selected', String(el === next));
    }

    const activeDescendantTarget = this.#getActiveDescendantTarget();
    activeDescendantTarget?.setAttribute('aria-activedescendant', next?.id ?? '');
    if (activeDescendantTarget instanceof HTMLElement && document.activeElement !== activeDescendantTarget) {
      this.#focusWithoutScroll(activeDescendantTarget);
    }
    this.#scrollItemIntoRegion(next, { block: 'nearest' });
  }

  resetCountriesFilter(event) {
    const { search } = this.refs;
    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.focus();
  }

  #onSearchKey = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.#submitSelectedCountry();
      return;
    }
    this.#onListKey(event);
  };

  resetForm() {
    const { search } = this.refs;
    if (!search || search.value === '') return;
    search.value = '';
    this.filterCountries();
  }

  focusSearchInput = () => this.refs.search?.focus();

  syncPrimaryControl = ({ block = 'center' } = {}) => {
    this.#syncActiveCountryItem();
    this.#revealSelectedCountryItem({ block });
  };

  focusPrimaryControl = ({ block = 'center' } = {}) => {
    this.syncPrimaryControl({ block });

    if (this.refs.search instanceof HTMLElement) {
      this.#focusWithoutScroll(this.refs.search);
    } else if (this.refs.countryList instanceof HTMLElement) {
      this.#focusWithoutScroll(this.refs.countryList);
    }
  };

  #onScroll = (event) => {
    const filter = this.querySelector('.country-filter');
    const list = event.target;
    if (filter && list) {
      filter.classList.toggle('is-scrolled', list.scrollTop > 0);
    }
  };
}

// Dropdown-style localization selector with panel show/hide
class DropdownLocalizationComponent extends FCElement {
  #ac = null;

  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('keyup', this.#onKey, { signal });
    document.addEventListener('click', this.#onOutsideClick, { signal });

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#syncWidth();
      this.refs.localizationForm?.focusPrimaryControl();
    });
  }

  hidePanel = () => {
    if (this.isHidden) return;
    this.#ac?.abort();
    this.#ac = null;
    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  #onOutsideClick = (event) => {
    if (isClickedOutside(event, this)) this.hidePanel();
  };

  #syncWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  #onKey = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

// Drawer-style localization selector using <details> toggle
class DrawerLocalizationComponent extends FCElement {
  #ac = null;

  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;
    if (!(target instanceof HTMLDetailsElement)) return;

    const list = localizationForm?.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      this.#ac?.abort();
      this.#ac = new AbortController();
      const { signal } = this.#ac;
      list?.addEventListener('scroll', (e) => requestAnimationFrame(() => this.#onScroll(e)), {
        passive: true,
        signal,
      });
    } else {
      this.#ac?.abort();
      this.#ac = null;
      localizationForm?.resetForm();
    }
  }

  #onScroll = (event) => {
    const filter = this.querySelector('.country-filter');
    const list = event.target;
    if (filter && list) {
      filter.classList.toggle('is-scrolled', list.scrollTop > 0);
    }
  };
}

if (!customElements.get('locale-form')) {
  customElements.define('locale-form', LocalizationFormComponent);
}

if (!customElements.get('locale-dropdown')) {
  customElements.define('locale-dropdown', DropdownLocalizationComponent);
}

if (!customElements.get('locale-drawer')) {
  customElements.define('locale-drawer', DrawerLocalizationComponent);
}
